import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import {
  Box,
  Heading,
  Stack,
  Text,
  TextLink,
  useResponsiveValue
} from '@machobear/design-system'

import { breakpoints } from '@/styles'
import globeIcon from '@/features/Company/icons/globe-icon.png'
import phoneIcon from '@/features/Company/icons/phone-icon.png'
import Head from '@/components/head'

import heroImg from './hero.jpg'
import titleLogo from './logo.png'

type Props = {
  metadata?: {
    __typename: string
    page: number
    page_size: number
    total_count: number
  }
  footerOnScreen: boolean
  listingsOnScreen: boolean
}

const GWOverlay = styled.div`
  max-width: 965px;
  width: calc(100% - 40px);
  padding: 60px 10px;
  text-align: center;
  background-color: rgba(3, 70, 56, 0.75);
  color: white;
  z-index: 1;
  overflow: hidden;
`
const GWHeading = styled.h1`
  font-size: 50px;
  font-weight: 500;
  margin-bottom: 20px;

  @media (max-width: ${breakpoints.phoneMax}) {
    font-size: 25px;
    margin-bottom: 0;
  }
`

const GWSubHeading = styled.h2`
  width: 100%;
  font-weight: 400;
  text-align: center;
  font-size: 23px;
  line-height: 1.5;
  font-weight: 500;
  margin: 0;

  @media (max-width: ${breakpoints.phoneMax}) {
    font-size: 14px;
  }
`

const GreenwinProfile = ({ metadata }: Props) => {
  const { t } = useTranslation('greenwin')
  const profile = {
    title: t('greenwin:Greenwin'),
    url: '/company/greenwin'
  }

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })

  const isDesktop = useResponsiveValue()({
    mobile: false,
    tablet: false,
    desktop: true
  })

  return (
    <>
      <Head
        title={`${profile.title} | liv.rent`}
        path={profile.url}
        pageData={metadata}
        hideCustomerSupport
      />
      <Box
        style={{
          height: isMobile ? '210px' : isDesktop ? '575px' : '350px'
        }}
        marginBottom="gutter"
        overflow="hidden"
        position="relative"
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="full"
          width="full"
          overflow="hidden"
        >
          <GWOverlay>
            <GWHeading>WELCOME HOME</GWHeading>
            <GWSubHeading>
              Creating vibrant housing communities since 1948.
            </GWSubHeading>
          </GWOverlay>
          <Image
            src={heroImg.src}
            alt="Greenwin"
            layout="fill"
            objectFit="cover"
          />
        </Box>
      </Box>
      <Box
        display="flex"
        justifyContent="center"
        width="full"
        marginBottom="large"
      >
        <Stack space="medium">
          <Box display="flex" alignItems="center" marginRight="large">
            <Box
              marginRight="xsmall"
              marginLeft="medium"
              style={{ flexShrink: 0 }}
            >
              <Image
                src={titleLogo.src}
                width={100}
                height={100}
                alt="Greenwin"
              />
            </Box>
            <Heading level="3" weight="weak">
              Greenwin Corp.
            </Heading>
          </Box>

          <Box
            display="flex"
            flexDirection={{ mobile: 'column', tablet: 'row' }}
            gap={{ mobile: 'gutter', tablet: 'none' }}
            marginX="medium"
            marginBottom="xlarge"
            marginTop="medium"
            style={{
              maxWidth: '1096px'
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              marginRight="xxlarge"
              style={{
                width: isMobile ? '100%' : 'fit-content'
              }}
            >
              <Stack space="medium">
                <Stack space="gutter">
                  <Text size="standard" weight="strong">
                    {t(`greenwin:subtitle`)}
                  </Text>
                  <Text size="standard">{t('greenwin:description1')}</Text>
                  <Text size="standard">{t('greenwin:description2')}</Text>
                  <Text size="standard">{t('greenwin:description3')}</Text>
                </Stack>
              </Stack>
            </Box>
            <Box
              background="neutralSoft"
              borderRadius="small"
              paddingY="small"
              paddingX="gutter"
              marginTop={{ mobile: 'gutter', tablet: 'none' }}
              style={{
                height: 'min-content',
                width: isMobile ? '100%' : 'fit-content',
                minWidth: '300px'
              }}
            >
              <Stack space="medium">
                <Box display="flex" alignItems="center" width="full">
                  <Box style={{ width: '24px' }}>
                    <Image src={phoneIcon.src} alt="" width={16} height={16} />
                  </Box>
                  <TextLink
                    href={`tel: (416)487-3883`}
                    rel="noreferrer noopener"
                  >
                    (416) 487-3883
                  </TextLink>
                  <Box marginLeft="xxxsmall">
                    <Text size="xsmall">(Head Office)</Text>
                  </Box>
                </Box>

                <Box display="flex" alignItems="center" width="full">
                  <Box style={{ width: '24px' }}>
                    <Image
                      src={globeIcon.src}
                      alt="link to greenwin website"
                      width={16}
                      height={16}
                    />
                  </Box>
                  <TextLink
                    href="https://www.greenwin.ca/"
                    rel="noreferrer noopener"
                    target="_blank"
                  >
                    https://www.greenwin.ca/
                  </TextLink>
                </Box>
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Box>
      <Box textAlign="center" paddingBottom="gutter">
        <Heading level="6">{t('greenwin:Our Portfolio')}</Heading>
      </Box>
    </>
  )
}

export default GreenwinProfile
